'use client';

import Link from 'next/link';
import React, {HTMLAttributes, ReactNode, useEffect, useState} from 'react';
import ChevronLeftIcon from '../Icons/ChevronLeft';
import {getTranslation} from '@/lib/utils';
import {Locale} from '../../../i18n.config';
import {IDiscoverSicilyDictionary} from '@/dictionaries/pages/discoverSicily/types';
import {FiChevronLeft, FiChevronRight} from 'react-icons/fi';

interface IStickyHeaderProps extends HTMLAttributes<HTMLDivElement> {
  backButtonText: string;
  backButtonHref?: string;
  headerClassName?: string;
  hasHeaderScroll?: boolean;
  mainContainerClassName?: string;
  tabs: ReactNode[];
  showIn?: 'desktop' | 'xl' | 'all';
  allwaysShow?: boolean;
}

export const StickyHeader: React.FC<IStickyHeaderProps> = ({
  backButtonText,
  backButtonHref = '',
  tabs,
  className,
  headerClassName,
  mainContainerClassName,
  hasHeaderScroll = false,
  allwaysShow = false,
  showIn = 'xl',
  children,
}) => {
  const [maxScreenWidth, setMaxScreenWidth] = useState(1920);
  const [headerTop, setHeaderTop] = useState<number | undefined>(undefined);
  const [footerTop, setFooterTop] = useState<number | undefined>(undefined);

  const preventBehavior = (e: TouchEvent) => {
    // e.preventDefault();
  };

  useEffect(() => {
    const headerElement = document.querySelector('#header-root');
    const mainFooter = document.querySelector('#main-footer');
    const body = document.body;

    setHeaderTop(
      (headerElement?.getBoundingClientRect().top || 0) -
        body.getBoundingClientRect().top,
    );
    setFooterTop(
      (mainFooter?.getBoundingClientRect().top || 0) -
        body.getBoundingClientRect().top,
    );

    window.scrollTo({top: 0});

    document.addEventListener('touchmove', preventBehavior);

    return () => {
      document.removeEventListener('touchmove', preventBehavior);
    };
  }, []);

  useEffect(() => {
    if (headerTop === undefined) return;
    const handleResize = () => {
      // Atualiza o estado com o width máximo da tela
      setMaxScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', isSticky);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', isSticky);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerTop, footerTop]);

  const isSticky = () => {
    const headerElement = document.querySelector('#header-root');
    const headerGhost = document.querySelector('#header-ghost');
    const scrollTop = window.scrollY;
    if (
      headerTop !== undefined &&
      scrollTop > headerTop &&
      footerTop !== undefined &&
      scrollTop < footerTop
    ) {
      headerElement?.classList.add('is-sticky');
      headerGhost?.classList.add('active');
    } else {
      headerElement?.classList.remove('is-sticky');
      headerGhost?.classList.remove('active');
    }
  };

  const scroll = (direction: 1 | -1) => {
    const headerScroll = document.querySelector('#header-scroll');
    if (headerScroll) {
      headerScroll.scrollLeft += (220 + 16) * direction;
    }
  };

  const breakpointStyle = {
    all: '[&.is-sticky]:fixed w-full [&.is-sticky]:h-20 z-[998] [&.is-sticky]:shadow-default',
    desktop:
      '[&.is-sticky]:desktop:fixed desktop:w-full [&.is-sticky]:desktop:h-20 desktop:z-[998] [&.is-sticky]:desktop:shadow-default',
    xl: '[&.is-sticky]:xl:fixed xl:w-full [&.is-sticky]:xl:h-20 xl:z-[998] [&.is-sticky]:xl:shadow-default',
  };

  return (
    // <div className={allwaysShow ? 'sticky top-0 bg-white z-10 !mt-0' : ''}>
    <div
      id="header-ghost"
      className={
        allwaysShow
          ? 'flex flex-row w-full !mt-0 items-center h-fit min-h-20'
          : ''
      }>
      <div
        id="header-root"
        className={`
        overflow-hidden relative top-0 left-0 [&.is-sticky]:bg-white bg-transparent transition-all
        duration-500 ease-in-out
        ${breakpointStyle[showIn]}
        ${!allwaysShow ? 'h-0 w-0 !fixed' : 'w-full'}
        ${headerClassName}
      `}>
        <div
          className={`
        w-full h-full max-w-[100%] px-sm md:px-md desktop:px-lg mx-auto
        flex flex-row items-center mb-4 pt-2 ${mainContainerClassName}
        `}>
          {!!backButtonText && (
            <Link
              className={`text-gray-700 mr-2 flex flex-row space-x-1 ${
                allwaysShow && 'self-start'
              } desktop:self-auto items-center`}
              href={backButtonHref}>
              <ChevronLeftIcon className="stroke-gray-700 w-6 pb-0.5" />
              <span>{backButtonText}</span>
            </Link>
          )}
          <div className={`flex flex-row w-full ${className} `}>
            {hasHeaderScroll && (
              <button type="button" onClick={() => scroll(-1)}>
                <div
                  className={`
                    w-[26px] h-[26px] rounded-full border border-gray-500 bg-white hidden justify-center items-center 
                  `}>
                  <FiChevronLeft className={`w-4 h-4 stroke-gray-500`} />
                </div>
              </button>
            )}
            {hasHeaderScroll && maxScreenWidth > 1200 ? (
              <div
                id="header-scroll"
                className={`flex scroll-smooth flex-row w-full items-center space-x-4 overflow-auto scrollbar-none mx-2.5 ${
                  hasHeaderScroll && 'desktop:w-[910px] space-x-8'
                }`}>
                {tabs}
              </div>
            ) : (
              tabs
            )}
            {hasHeaderScroll && (
              <button type="button" onClick={() => scroll(1)}>
                <div
                  className={`
                    w-[26px] h-[26px] rounded-full border border-gray-500 bg-white hidden justify-center items-center
                    ${hasHeaderScroll && 'desktop:flex'}
                  `}>
                  <FiChevronRight className={`w-4 h-4 stroke-gray-500`} />
                </div>
              </button>
            )}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};
